import { useQuery } from '@tanstack/react-query';
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { Activity, ActivityRequest, FoundRow, MissEvidence, Verdict, ActivityResult, ActivitySource, FailureKey } from '@claims/core';
import { activityCounts, dayLabel, dolLabel, FEEDBACK_LABEL, FEEDBACK_ORDER, inState, matchesWords, openedShort, pacificClock, pacificDays, searchWordsOf, SOURCE_DETAIL, TREND_DAYS } from '@claims/core';
import { countOf } from './count.js';
import { api } from './api.js';
import { clientLine, SidePill } from './components.js';
import { emojiOf, Glyph } from './icons.js';
import { FRAME_SEARCH, Shell, Topbar } from './Shell.js';
import { StateSeg, useStateChoice } from './state-seg.js';
import { EmptyState, QueryState, STATE_WORDS } from './states.js';
import { isDefault, pressSort, sortedLine, sortOf, sortRows, writeSort, type ListSort, type ListSortSpec, type SortValue } from './list/list-sort.js';
import { KEY_WORDS, lettersOf, SF_COLUMN_WORDS, sfHrefs, sfKeyWords, SIDE_KEY_WORDS, sideTag, type KeyEntry } from './list/row-key.js';
import { RowKey } from './list/RowKey.js';
import { FoundChip } from './found.js';
import { EvidenceNotice, MissDetail, VerdictBadge, VERDICT_WORDS, type EvidenceStatus } from './today-miss.js';
import { useRowsChangedGuard } from './list/hold.js';
import { SfLinks } from './list/SfLinks.js';
import { PlainHeader, SortedLine, SortHeader } from './list/SortHeader.js';

// "What is happening today" (Rod 2026-10-08): every AI claim call request created today, who started it, and what came of it.
const RESULT: Record<ActivityResult, { label: string; cls: string; icon: string }> = {
  success: { label: 'Got a claim #', cls: 'stc-opened', icon: 'win' }, miss: { label: 'Miss', cls: 'stc-need', icon: 'alert' },
  running: { label: 'Calling', cls: 'stc-working', icon: 'hourglass' }, waiting: { label: 'Waiting 7 am', cls: 'stc-working', icon: 'hourglass' },
  stopped: { label: 'Stopped', cls: 'stc-none', icon: 'minus' }, never_ran: { label: 'No result', cls: 'stc-need', icon: 'alert' },
};
const SRC_CLS: Record<ActivitySource, string> = {
  sweeper_auto: 'a-sweep', sweeper_click: 'a-sweep', router_click: 'a-router', controller: 'a-ctl', ins_creation: 'a-insc', staff: 'a-staff',
  other_auto: 'a-other', automation_unsplit: 'a-other',
};

/** The top bar's search (core list_search.ts, the medrec console's one rule): every typed word somewhere in the row's words. */
function matches(r: ActivityRequest, q: string): boolean {
  return matchesWords([r.name, r.matter?.client, r.matter?.name, r.matter ? clientLine(r.matter) : null, r.carrier, r.startedBy, r.startedByDetail, r.resultText], searchWordsOf(q));
}
/** change 7: the approval cards waiting in Slack, in plain words (the system's "sweeper card" name stays in the hover) */
export function cardsLine(c: { sweeper: number; other: number }): string {
  const n = c.sweeper + c.other;
  if (!n) return 'No AI calls waiting for approval in Slack';
  const parts = [c.sweeper ? `${c.sweeper} found in the case files` : '', c.other ? `${c.other} from new documents` : ''].filter(Boolean).join(', ');
  return `${n} AI call${n === 1 ? '' : 's'} waiting for someone to approve in Slack (${parts})`;
}

export function Band({ a, pick, setPick, oneState }: { a: Activity; pick: ActivitySource | null; setPick: (s: ActivitySource | null) => void; oneState: boolean }) {
  // the approvals waiting in Slack are read live and their window spills past the day: on a past day that number is not that day's
  const past = a.label !== 'Today';
  return (
    <section className="act-band" aria-label={`${past ? a.label : 'Today'} by who started the call`}>
      <div className="act-tot">
        <span className="sc-h">{past ? `AI claim calls ${a.label}` : 'AI claim calls today'}</span>
        {/* change 8 (Kimi's UI assessment, Rod 2026-10-13 "1 a"): this counts CALLS that got a claim number; the Matters tile counts each
            record's first claim number of the day once and leaves out second clients, so the two can differ by a few */}
        <span className="sc-n"><b>{countOf(a.totals.requests)}</b><span>requests</span><b className="sm">{countOf(a.totals.placed)}</b><span>calls placed</span>
          <b className="sm ok">{countOf(a.totals.claims)}</b><span title="Counted per call. The Matters tile counts each record's first claim number of the day once and leaves out second clients from the same crash, so the two numbers can differ by a few.">calls got a claim number</span></span>
        {a.totals.stopped ? <span className="act-cards" title="Requests that never dialed: State Farm online filing held, or a safety check stopped the call">
          {countOf(a.totals.stopped)} stopped before dialing ({a.totals.onlineHeld} State Farm online held, {a.totals.blocked} blocked{a.totals.stopped - a.totals.onlineHeld - a.totals.blocked > 0 ? `, ${a.totals.stopped - a.totals.onlineHeld - a.totals.blocked} never ran` : ''})</span> : null}
        {past && a.cardsWaiting ? null : a.cardsWaiting ? <span className="act-cards" title={`${a.cardsWaiting.sweeper} sweeper card(s) and ${a.cardsWaiting.other} other card(s) still waiting for a click`}>
            {cardsLine(a.cardsWaiting)}{oneState ? ' (every state)' : ''}</span>
          : <span className="act-cards mt" title="Sweeper split and cards waiting: needs read access to the router ledger (migration 002)">Who started some calls, and approvals waiting, are not readable right now</span>}
      </div>
      <div className="act-srcs">
        {a.bySource.map((g) => (
          <button key={g.source} type="button" className={`act-src ${SRC_CLS[g.source]}`} aria-pressed={pick === g.source} onClick={() => setPick(pick === g.source ? null : g.source)} title={SOURCE_DETAIL[g.source]}>
            <i aria-hidden="true" /><span className="as-l">{g.label}</span>
            <span className="as-n"><b>{g.requests}</b> req · <b>{g.placed}</b> placed · <b className="ok">{g.claims}</b> claim #s</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** The address's own filters (`?day=&result=&reason=`); anything it does not know reads as none. */
export type TodayResultFilter = 'miss' | 'notsent';
export function todayFiltersOf(sp: URLSearchParams): { day: string | null; result: TodayResultFilter | null; reason: FailureKey | null } {
  const day = sp.get('day'), result = sp.get('result'), reason = sp.get('reason');
  return {
    day: day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null,
    result: result === 'miss' || result === 'notsent' ? result : null,
    reason: (FEEDBACK_ORDER as string[]).includes(reason ?? '') ? (reason as FailureKey) : null,
  };
}
/** Misses = Disposition Miss; never went out = notSent; a reason = the dialed misses with that key (a reason implies a miss). */
export function filterRequests(rows: ActivityRequest[], f: { result: TodayResultFilter | null; reason: FailureKey | null }): ActivityRequest[] {
  return rows.filter((r) => (f.result === 'notsent' ? r.notSent : f.result === 'miss' || f.reason ? r.result === 'miss' : true) && (!f.reason || r.reason === f.reason));
}

/** `?have=`: the "Did we have it?" filter. Carrier side has no verdict to pick, so it is not an option. */
export type HaveFilter = Exclude<Verdict, 'carrier_side'>;
export const HAVE_ORDER: readonly HaveFilter[] = ['in_files_before', 'fixed_after', 'not_in_readable_files', 'cant_tell'];
export function haveOf(sp: URLSearchParams): HaveFilter | null {
  const v = sp.get('have');
  return (HAVE_ORDER as readonly string[]).includes(v ?? '') ? (v as HaveFilter) : null;
}
/** A verdict hides every other verdict and every row with no evidence (yet); no verdict shows all. */
export function filterByHave(rows: ActivityRequest[], have: HaveFilter | null, map: Record<string, MissEvidence> | undefined): ActivityRequest[] {
  return have ? rows.filter((r) => map?.[r.canId]?.verdict === have) : rows;
}
export interface TodayEvidence { map: Record<string, MissEvidence> | undefined; status: EvidenceStatus }

// ---- the call list, in the medrec console's style B (2026-10-18; DESIGN_SYSTEM 7, 7a, 8) ----------------------------------------------
/** THE SORT: every column, the address keeping it (`?sort=&dir=`, list/list-sort.ts); no column is the server's own order. */
export type TodaySortKey = 'time' | 'case' | 'side' | 'carrier' | 'who' | 'result' | 'what';
export const TODAY_SORT: ListSortSpec<TodaySortKey> = Object.freeze({
  keys: ['time', 'case', 'side', 'carrier', 'who', 'result', 'what'] as readonly TodaySortKey[],
  default: { key: null, dir: 'asc' } as ListSort<TodaySortKey>,
  words: {
    time: { name: 'Time', asc: 'earliest first', desc: 'latest first' }, case: { name: 'Case', asc: 'A to Z', desc: 'Z to A' },
    side: { name: 'Side', asc: 'A to Z', desc: 'Z to A' }, carrier: { name: 'Carrier', asc: 'A to Z', desc: 'Z to A' },
    who: { name: 'Started by', asc: 'A to Z', desc: 'Z to A' }, result: { name: 'Result', asc: 'A to Z', desc: 'Z to A' },
    what: { name: 'What happened', asc: 'A to Z', desc: 'Z to A' },
  },
});
export function todaySortValue(r: ActivityRequest, k: TodaySortKey): SortValue {
  switch (k) {
    case 'time': { const t = Date.parse(r.at); return Number.isFinite(t) ? t : null; }
    case 'case': return r.matter?.client ?? r.matter?.name ?? null;
    case 'side': return r.side;
    case 'carrier': return r.carrier;
    case 'who': return r.startedBy;
    case 'result': return RESULT[r.result].label;
    case 'what': return r.resultText;
  }
}

/** A ROW'S KEY (the circle i): only the marks the row carries, in the order they sit on it. Today's own words; the sides and M and I are
 *  the shared dictionary's (list/row-key.ts). */
export const TODAY_KEY_WORDS = Object.freeze({
  time: 'When the call request was made (Pacific time).',
  result: {
    success: 'The call got a claim number.', miss: 'The call ended without a claim number; What happened says why.', running: 'The AI is on the call now.',
    waiting: 'It waits for 7 am Pacific, when calls may start.', stopped: 'Stopped before dialing: State Farm online filing, or a safety check.',
    never_ran: 'The request never ran.',
  } as Readonly<Record<ActivityResult, string>>,
  repeat: (m: number): string => `Another request on the same record ${m} minute${m === 1 ? '' : 's'} before this one.`,
});
export function todayKeyEntries(r: ActivityRequest): KeyEntry[] {
  const st = RESULT[r.result];
  const out: KeyEntry[] = [{ id: 'time', mark: { kind: 'text', text: pacificClock(r.at) }, words: TODAY_KEY_WORDS.time }];
  if (r.side) out.push({ id: 'side', mark: { kind: 'tag', words: sideTag(r.side) }, words: SIDE_KEY_WORDS[r.side] ?? `${r.side}.` });
  out.push({ id: 'result', mark: { kind: 'tag', icon: emojiOf(st.icon), words: st.label }, words: TODAY_KEY_WORDS.result[r.result] });
  if (r.repeatOfMinutes !== null) out.push({ id: 'repeat', mark: { kind: 'tag', words: `repeat +${r.repeatOfMinutes} m` }, words: TODAY_KEY_WORDS.repeat(r.repeatOfMinutes) });
  if (r.matterId) { const letters = lettersOf(sfHrefs(r.matterId, r.insuranceId)); out.push({ id: 'links', mark: { kind: 'links', letters }, words: sfKeyWords(letters) }); }
  return out;
}

/** The Found-a-number row a Today miss shows (the same answer as the Queue's group): the record's anchor miss by its request, or, once the record
 *  has a status, any later miss of that record (the approved call's own miss shows how it went). */
export function foundFor(r: ActivityRequest, rows: FoundRow[] | undefined): FoundRow | undefined {
  if (!rows || r.result !== 'miss' || !r.insuranceId) return undefined;
  const ins = r.insuranceId.slice(0, 15);
  const at = Date.parse(r.at.replace(/\+0000$/, 'Z'));
  return rows.find((x) => x.canId === r.canId) ?? rows.find((x) => !!x.status && x.insuranceId.slice(0, 15) === ins && at > Date.parse(x.at));
}

function Row({ r, evidence, found }: { r: ActivityRequest; evidence?: TodayEvidence; found?: { rows: FoundRow[]; approveOn: boolean } }) {
  const ev = evidence?.map?.[r.canId];
  const f = foundFor(r, found?.rows);
  const st = RESULT[r.result];
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
    <tr className="arow lp-r">
      <td className="lp-since">{pacificClock(r.at)}</td>
      <td className="t-case">
        <span className="lp-l1">{r.matterId ? <Link className="sflink" to={`/matters/${r.matterId}`} title={r.matter?.client ?? r.matter?.name ?? undefined}><b className="lp-name">{r.matter?.client ?? r.matter?.name ?? 'Case'}</b></Link> : <b className="lp-name">No case</b>}</span>
        <span className="lp-l2">{r.matter ? clientLine(r.matter, r.matter.convertedAt ? openedShort(r.matter.convertedAt, new Date()) : null, r.name) : r.name}</span>
      </td>
      <td>{r.side ? <SidePill side={r.side} /> : null}</td>
      <td className="t-car">{r.carrier ? <b>{r.carrier}</b> : <span className="mt">no carrier</span>}</td>
      <td title={r.startedByDetail ?? r.startedBy}><span className={`act-who ${SRC_CLS[r.source]}`}><i aria-hidden="true" /><span className="aw-t">{r.startedBy}</span>{r.repeatOfMinutes !== null ? <span className="tag2" title="Another request on the same record shortly before">repeat +{r.repeatOfMinutes} m</span> : null}</span></td>
      <td><span className={`stc ${st.cls}`}><Glyph name={st.icon} /><span>{st.label}</span></span></td>
      <td className="t-what" title={r.resultText}>{r.result === 'miss'
        ? <button type="button" className="tm-open" aria-expanded={open} onClick={() => setOpen(!open)}>{r.resultText}</button> : r.resultText}
        {r.result === 'miss' && evidence && r.dialed === true && !r.notSent ? <VerdictBadge ev={ev} status={evidence.status} /> : null}
        {f ? <> <FoundChip row={f} /></> : null}</td>
      <td className="tl-sf">{r.matterId ? <SfLinks hrefs={sfHrefs(r.matterId, r.insuranceId)} /> : null}</td>
      <td className="tl-key"><RowKey name={KEY_WORDS.buttonName(r.matter?.client ?? r.name)} entries={todayKeyEntries(r)} /></td>
    </tr>
    {open ? <tr className="tm-row"><td colSpan={9}><MissDetail r={r} ev={ev} found={f} approveOn={found?.approveOn === true} /></td></tr> : null}
    </Fragment>
  );
}

/** The table: its sortable header (the shown count beside Case), then one row per call request, in the sort. */
export function TodayTable({ rows, sort, onSort, count, empty, evidence, found }: { evidence?: TodayEvidence; found?: { rows: FoundRow[]; approveOn: boolean }; rows: ActivityRequest[]; sort: ListSort<TodaySortKey>; onSort: (k: TodaySortKey) => void; count: string; empty?: ReactNode }) {
  const h = (label: string, k: TodaySortKey, cls: string) => <SortHeader label={label} sortKey={k} sort={sort} onSort={onSort} className={cls} />;
  const sorted = useMemo(() => sortRows(rows, sort, todaySortValue), [rows, sort]);
  // A CLICK THE ROWS MOVED UNDER IS DROPPED (list/hold.ts, the medrec console's post-ship review): every 2 minutes the next read adds calls
  // and re-sorts, and the row under the pointer would change in the half second a click needs
  const guard = useRowsChangedGuard(useMemo(() => sorted.map((r) => JSON.stringify(r)).join('\n'), [sorted]));
  return (
    <div className="qcard">
      <table className="lp-table ttable" onClickCapture={guard}>
        <colgroup><col className="tc-time" /><col className="tc-case" /><col className="tc-side" /><col className="tc-car" /><col className="tc-who" /><col className="tc-res" /><col /><col className="tc-sf" /><col className="tc-key" /></colgroup>
        <thead><tr>
          {h('Time', 'time', 'th-time')}{h(`Case · ${count}`, 'case', 'th-case')}{h('Side', 'side', 'th-side')}{h('Carrier', 'carrier', 'th-car')}
          {h('Started by', 'who', 'th-who')}{h('Result', 'result', 'th-res')}{h('What happened', 'what', 'th-what')}<PlainHeader label={SF_COLUMN_WORDS} /><PlainHeader label={KEY_WORDS.column} />
        </tr></thead>
        <tbody>
          {sorted.length ? sorted.map((r) => <Row key={r.canId} r={r} evidence={evidence} found={found} />) : <tr><td colSpan={9} className="t-wide">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/** The day's read (Cesar 2026-10-22): the shown day stays on screen while the next one loads, so the band, the selects and the table stay
 *  mounted and the Day select keeps its focus; only today refreshes itself (a past day is over). */
export function activityQuery(day: string | null, today: string) {
  return {
    queryKey: ['activity', day ?? 'today'],
    queryFn: () => api.activity(day ?? undefined),
    staleTime: 60_000,
    refetchInterval: !day || day === today ? 120_000 : (false as const),
    placeholderData: <T,>(prev: T | undefined) => prev,
  };
}
/** Says which day is loading while the previous one is still shown (a live region that is always there, so the words are announced). */
export function DayLoading({ loading, label }: { loading: boolean; label: string }) {
  return <div className="tm-loading" role="status">{loading ? <p className="mt">{`Loading ${label}\u2026`}</p> : null}</div>;
}

/** The verdicts beside the day's read: they never hold the rows back (a failed read leaves them badge-less). */
export function evidenceQuery(day: string | null, today: string) {
  return {
    queryKey: ['miss-evidence', day ?? 'today'],
    queryFn: () => api.missEvidence(day ?? undefined),
    staleTime: 60_000,
    refetchInterval: !day || day === today ? 120_000 : (false as const),
  };
}

export function TodayPage() {
  const [q, setQ] = useState('');
  const [pick, setPick] = useState<ActivitySource | null>(null);
  const [stateChoice, setStateChoice] = useStateChoice();
  // the list's sort, in the address (one pair for this page)
  const [sp, setSp] = useSearchParams();
  const sort = sortOf(sp, TODAY_SORT);
  const f = todayFiltersOf(sp);
  const have = haveOf(sp);
  const setParam = (k: 'day' | 'result' | 'reason' | 'have', v: string | null) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const days = useMemo(() => pacificDays(new Date(), TREND_DAYS).reverse().map((x, i) => ({ day: x.day, label: i === 0 ? `${dayLabel(x.day).label} · today` : dayLabel(x.day).label })), []);
  const onSort = (k: TodaySortKey) => setSp(writeSort(sp, pressSort(sort, k), TODAY_SORT), { replace: true });
  const d = useQuery(activityQuery(f.day, days[0]!.day));
  const ev = useQuery(evidenceQuery(f.day, days[0]!.day));
  const fq = useQuery({ queryKey: ['found-number'], queryFn: api.foundNumber, staleTime: 120_000, refetchInterval: 300_000 });
  const evidence: TodayEvidence = { map: ev.data?.evidence, status: ev.isError && !ev.data ? 'error' : ev.data ? 'ok' : 'loading' };
  // the State row: the band's numbers and the rows are that state's (activityCounts, the server's own rule); All = the server's answer as is
  const a = useMemo(() => {
    const all = d.data;
    if (!all || stateChoice === null) return all;
    const requests = all.requests.filter((r) => inState(r.matter?.state, stateChoice));
    return { ...all, requests, ...activityCounts(requests) };
  }, [d.data, stateChoice]);
  const rows = useMemo(() => (a ? filterByHave(filterRequests(a.requests, f).filter((r) => (!pick || r.source === pick) && matches(r, q)), have, ev.data?.evidence) : []), [a, pick, q, f.result, f.reason, have, ev.data]);
  return (
    <Shell>
      <Topbar title="Today" sub={d.data && d.data.label !== 'Today' ? `${d.data.label} · every AI claim call started that day` : 'every AI claim call started today'} asOf={d.data?.asOf} q={q} setQ={setQ} ph={FRAME_SEARCH.today}
        stale={d.isRefetchError} onRetry={() => d.refetch()} />
      <div className="content tight shadcn">
        <QueryState q={d} what="today's calls" loading="Loading today's calls from Salesforce and the claims system." rows />
        <DayLoading loading={d.isPlaceholderData} label={f.day && f.day !== days[0]!.day ? dayLabel(f.day).label : 'today'} />
        {d.data?.note ? <p className="mt">{d.data.note}</p> : null}
        <EvidenceNotice failed={ev.isError && !ev.data} notes={ev.data?.notes} />
        {a ? (
          <>
            <Band a={a} pick={pick} setPick={setPick} oneState={stateChoice !== null} />
            <div className="listbar"><StateSeg states={(d.data?.requests ?? []).map((r) => r.matter?.state)} choice={stateChoice} onChoose={setStateChoice} />
              <label className="qsel"><select aria-label="Day" value={f.day && days.some((x) => x.day === f.day) && f.day !== days[0]?.day ? f.day : ''} onChange={(e) => setParam('day', e.target.value || null)}>
                {days.map((x, i) => <option key={x.day} value={i === 0 ? '' : x.day}>{x.label}</option>)}</select></label>
              <label className="qsel"><select aria-label="Result" value={f.result ?? ''} onChange={(e) => setParam('result', e.target.value || null)}>
                <option value="">All calls</option><option value="miss">Misses</option><option value="notsent">Never went out</option></select></label>
              <label className="qsel"><select aria-label="Miss reason" value={f.reason ?? ''} onChange={(e) => setParam('reason', e.target.value || null)}>
                <option value="">Any reason</option>{FEEDBACK_ORDER.map((k) => <option key={k} value={k}>{FEEDBACK_LABEL[k]}</option>)}</select></label>
              <label className="qsel"><select aria-label="Did we have it?" value={have ?? ''} onChange={(e) => setParam('have', e.target.value || null)}>
                <option value="">Did we have it? All</option>{HAVE_ORDER.map((k) => <option key={k} value={k}>{VERDICT_WORDS[k]}</option>)}</select></label>
              <span className="grow" /><SortedLine words={sortedLine(sort, TODAY_SORT, false)} isDefault={isDefault(sort, TODAY_SORT)} onClear={() => setSp(writeSort(sp, TODAY_SORT.default, TODAY_SORT), { replace: true })} /></div>
            <TodayTable rows={rows} sort={sort} onSort={onSort} evidence={evidence} found={fq.data ? { rows: fq.data.rows, approveOn: fq.data.approveOn === true } : undefined} count={`${rows.length}${pick || stateChoice || f.result || f.reason || have ? ' shown' : ''}`}
              empty={q ? <EmptyState inline words={STATE_WORDS.noMatch(q)} next={{ label: STATE_WORDS.clearSearch, onPress: () => setQ('') }} />
                : <EmptyState inline words={a.requests.length ? 'No call matches the filters.' : (a.label === 'Today' ? 'No calls yet today.' : 'No calls that day.')} />} />
          </>
        ) : null}
      </div>
    </Shell>
  );
}
